<template>
  <!-- 편집 불가: 정적 표시 -->
  <span v-if="!editable" class="status-pill" :class="`sp-${status}`">
    <span class="sp-dot" />{{ label }}
  </span>

  <!-- 데스크톱: 드롭다운으로 즉시 변경 -->
  <el-dropdown
    v-else-if="isDesktop"
    trigger="click"
    @command="onCommand"
    @click.stop
  >
    <button type="button" class="status-pill is-clickable" :class="`sp-${status}`" @click.stop>
      <span class="sp-dot" />{{ label }}
      <el-icon class="sp-caret"><ArrowDown /></el-icon>
    </button>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="s in STATUS_LIST"
          :key="s.value"
          :command="s.value"
          :disabled="s.value === status"
        >
          <span class="sp-menu-dot" :style="{ background: s.color }" />{{ s.label }}
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>

  <!-- 모바일: 탭하면 부모가 시트를 연다 -->
  <button
    v-else
    type="button"
    class="status-pill is-clickable"
    :class="`sp-${status}`"
    @click.stop="$emit('request-change')"
  >
    <span class="sp-dot" />{{ label }}
    <el-icon class="sp-caret"><ArrowDown /></el-icon>
  </button>
</template>

<script>
import { computed } from 'vue'
import { ArrowDown } from '@element-plus/icons-vue'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { SELECTABLE_STATUSES, getStatusLabel } from '@/utils/taskStatus'

export default {
  name: 'StatusPill',
  components: { ArrowDown },
  props: {
    status: { type: String, default: 'pending' },
    editable: { type: Boolean, default: false }
  },
  emits: ['change', 'request-change'],
  setup(props, { emit }) {
    const { isDesktop } = useBreakpoint()
    const label = computed(() => getStatusLabel(props.status))
    function onCommand(cmd) {
      if (cmd !== props.status) emit('change', cmd)
    }
    return { isDesktop, label, STATUS_LIST: SELECTABLE_STATUSES, onCommand }
  }
}
</script>

<style scoped>
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: var(--r-full);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  line-height: 1.4;
  border: 1px solid transparent;
  white-space: nowrap;
}
.is-clickable { cursor: pointer; min-height: 28px; position: relative; }
/* 탭 타겟 확장. 시각 크기 28px 은 카드 밀도 때문에 그대로 두고 히트 영역만
 * --tap-min(44px) 으로 넓힌다. ::after 는 흐름 밖이라 레이아웃은 변하지 않는다.
 *
 * 이 규칙은 TaskCard.vue 에 :deep() 으로 있었는데, 그러면 .tc-meta 안의 필만
 * 44px 이 되고 TaskDetailPanel.vue:22 의 편집 가능한 필은 28px 그대로 남는다.
 * 근본 자리인 여기로 옮긴다.
 *
 * 위아래로 (44-28)/2 = 8px 씩 나간다. 이 필이 클릭 가능한 두 곳 모두 8px 이상
 * 비어 있어 이웃 텍스트 박스를 덮지 않는다:
 *   TaskCard    .tc-main gap 8px · .tc-meta row-gap 8px
 *   TaskDetail  .dp-meta gap 8px · margin-top 12px · .dp-desc margin-top 14px
 * Today.vue / AgendaDaySheet.vue 는 editable 을 넘기지 않아 .is-clickable 이
 * 아니므로(정적 span) 영향이 없다.
 *
 * 대가: TaskCard 에서 필은 @click.stop 이므로 필 폭(승인대기 기준 약 98px)
 * × 위아래 8px 띠를 탭하면 카드가 열리는 대신 상태 시트가 열린다. 그 띠는
 * 전부 gap 이라 제목/설명/푸터 텍스트는 그대로 카드를 연다. */
.is-clickable::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: var(--tap-min);
  transform: translateY(-50%);
}

.sp-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.sp-caret { font-size: var(--ic-xs); opacity: 0.7; }

/* 색은 --task-* 토큰만 쓴다. 하드코딩돼 있던 값들은 카드 액센트·보드 칩과
   같은 상태인데도 미묘하게 다른 색을 냈다.
   .sp-pending_approval 은 **규칙 자체가 없어서** 승인대기 필이 배경도 점도 없이
   맨 텍스트로 렌더됐다 — 이 필을 쓰는 6개 화면 전부에서. */
.sp-pending { background: var(--task-pending-bg); color: var(--task-pending-fg); }
.sp-pending .sp-dot { background: var(--task-pending); }
.sp-in_progress { background: var(--task-in-progress-bg); color: var(--task-in-progress-fg); }
.sp-in_progress .sp-dot { background: var(--task-in-progress); }
.sp-pending_approval { background: var(--task-pending-approval-bg); color: var(--task-pending-approval-fg); }
.sp-pending_approval .sp-dot { background: var(--task-pending-approval); }
.sp-done { background: var(--task-done-bg); color: var(--task-done-fg); }
.sp-done .sp-dot { background: var(--task-done); }

.sp-menu-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 8px; }
</style>
